import {
  AppResponse,
  BaseResponse,
  Card,
  CardResponse,
  Content,
  ControlResponse,
  HistoryResponse,
  Language,
  Operation,
  Tab,
  View,
  ViewResponse,
} from '@yleisradio/areena-types';
import { areenaApi } from '../../client/environment';
import {
  AddFavoriteError,
  NotFoundError,
  RemoveFavoriteError,
} from '../../client/error/errors';
import { logError } from '../../client/error/errorUtils';
import {
  AreenaApiRequiredParams,
  AreenaApiVersion,
  Cookies,
  PaginationData,
  PlaybackHistoryEntries,
} from '../../client/appTypings';
import {
  isContentKnown,
  isHighlightCard,
  isList,
} from '../../client/typeGuards';
import promiseWithTimeout from '../../client/utils/promiseWithTimeout';
import { App } from '@yleisradio/areena-types/domain/app';
import { areenaApiErrorHandler } from './areenaApiErrorHandler';
import { AuthFetch } from '../../client/domain/AuthFetch';
import clamp from '../../client/utils/clamp';
import { isoDurationToSeconds } from '../../client/utils/duration';
import { AreenaApiClient, LoggedInUser } from '../../common/types';
import { getUrlWithAuthentication } from '../utils';
import { httpFetch } from '../httpFetch';
import { getClientYleApiCredentials } from '../../client/utils/authKeys';
import { chunks } from '../../common/utils';
import { logger } from '../../common/logger';

const apiCookie = (cookies: Cookies | undefined): string | null => {
  if (!cookies) return null;

  const forwardableCookies = [
    'ylelogin',
    'yletestlogin',
    'yle_rec',
    'areena_ab',
    'userconsent',
  ];

  return Object.entries<string>(cookies)
    .filter(([name]) => forwardableCookies.includes(name))
    .reduce(
      (acc, [name, value]) => `${acc}${acc ? '; ' : ''}${name}=${value}`,
      ''
    );
};

export function isAreenaUrl(url: string) {
  return url.includes(areenaApi.url);
}

/**
 * @deprecated use getCommonUiApiParameters with url to add parameters
 * @type {{v: '10' | 'edge', language: string, client: 'yle-areena-leanback'}}
 */
export const areenaApiRequiredParamsForTest: AreenaApiRequiredParams = {
  v: areenaApi.uiVersion as AreenaApiVersion,
  language: 'fi',
  client: areenaApi.uiClient as AreenaApiClient,
};

/**
 * @deprecated use getCommonUiApiParameters with url to add parameters
 * @param client
 * @param language
 * @param areenaApiVersion
 * @returns {{v: '10' | 'edge', language: 'sv' | 'fi', client: 'yle-areena-leanback'}}
 */
export function areenaApiRequiredParams(
  client: AreenaApiClient,
  language: Language,
  areenaApiVersion: AreenaApiVersion
): AreenaApiRequiredParams {
  return {
    v: areenaApiVersion,
    language,
    client,
  };
}

export function getCommonUiApiParameters(language: Language): string {
  return `language=${language}&client=yle-areena-leanback&v=10`;
}

/**
 * Fetcher for SWR
 * @param config
 * @returns {Promise<ResponseType>}
 */
async function areenaApiFetch(config: {
  url: string;
  ignoreBody?: true;
  isUserLoggedIn?: boolean;
  requestInit?: RequestInit;
  silentlyConsumedStatusCodes?: number[];
}): Promise<void>;
async function areenaApiFetch<ResponseType extends BaseResponse>(config: {
  url: string;
  ignoreBody?: boolean;
  isUserLoggedIn?: boolean;
  requestInit?: RequestInit;
  silentlyConsumedStatusCodes?: number[];
}): Promise<ResponseType>;
async function areenaApiFetch<ResponseType extends BaseResponse>(config: {
  url: string;
  ignoreBody?: boolean;
  isUserLoggedIn?: boolean;
  requestInit?: RequestInit;
  silentlyConsumedStatusCodes?: number[];
}): Promise<ResponseType | void> {
  const {
    url,
    requestInit,
    ignoreBody = false,
    isUserLoggedIn = false,
    silentlyConsumedStatusCodes,
  } = config;

  const yleApiCredentials = getClientYleApiCredentials();

  if (!yleApiCredentials.id || !yleApiCredentials.key) {
    throw new Error('Missing YleApiCredentials.');
  }
  if (!url) {
    throw new Error('URL is required to fetch data from Areena API');
  }

  const urlWithAuth = getUrlWithAuthentication(
    url,
    yleApiCredentials.id,
    yleApiCredentials.key
  );

  const response = await fetch(urlWithAuth, {
    credentials: 'include',
    ...requestInit,
  }).catch(error => {
    console.error(`Failed to fetch ${url} ${error}`);
    throw error;
  });

  return await areenaApiErrorHandler<ResponseType>({
    response,
    url,
    ignoreBody,
    isUserLoggedIn,
    silentlyConsumedStatusCodes,
  });
}

export async function fetchAppData(
  currentUser: LoggedInUser | null | undefined,
  language: Language
): Promise<App> {
  let url: string;
  if (!!currentUser?.isSubAccount && currentUser?.age != undefined) {
    url = `${areenaApi.url}/v1/ui/age-${clamp(currentUser.age, 0, 99)}-app.json?${getCommonUiApiParameters(language)}`;
  } else {
    url = `${areenaApi.url}/v1/ui/app.json?${getCommonUiApiParameters(language)}`;
  }

  const { data } = await areenaApiFetch<AppResponse>({
    url,
  });

  if (!data) {
    throw new Error('No app data');
  }

  return data;
}

export async function fetchView(url: string): Promise<View> {
  const { data } = await areenaApiFetch<ViewResponse>({
    url,
  });

  if (!data) {
    throw new Error('No view data');
  }

  const filteredData = {
    ...data,
    tabs: data.tabs?.map(tab => {
      return {
        ...tab,
        content: tab.content?.filter(item => isContentKnown(item)),
      };
    }),
  };

  return filteredData;
}

export async function fetchList(url: string): Promise<CardResponse> {
  if (!url) {
    throw new Error('URL is required to fetch list data');
  }

  const response = await areenaApiFetch<CardResponse>({
    url,
  });

  return { data: response.data, meta: response.meta };
}

export async function fetchControl(
  url: string
): Promise<ControlResponse | null> {
  if (!url) {
    throw new Error('Pointer destination URI is required to fetch control');
  }
  const response = await areenaApiFetch<ControlResponse>({ url });

  return response;
}

export async function executeOperation(
  operation: Operation | undefined
): Promise<ControlResponse | null> {
  if (!operation) {
    throw new Error('Operation is required to execute operation');
  }
  if (!operation?.uri) {
    throw new Error(
      'Operation destination URI is required to execute operation'
    );
  }
  try {
    const response = await areenaApiFetch<ControlResponse>({
      url: operation.uri,
      requestInit: {
        method: operation.method,
        headers: operation.headers as Record<string, string>,
        body: operation.body,
      },
    });
    return response;
  } catch (error) {
    if (operation.method === 'DELETE') {
      logger.error(`Failed to remove favorite with operation ${error}`);
      throw new RemoveFavoriteError();
    } else {
      logger.error(`Failed to add favorite with operation ${error}`);
      throw new AddFavoriteError();
    }
  }
}

export async function getLivePlayerView(
  authFetch: AuthFetch,
  serverCookies: Cookies | undefined,
  serviceId: string,
  programId?: string,
  publicationId?: string
): Promise<ViewResponse> {
  if (programId && publicationId) {
    return getViewByPath(
      authFetch,
      `services/${serviceId}/player/${programId}/${publicationId}`,
      serverCookies
    );
  } else {
    return getViewByPath(
      authFetch,
      `services/${serviceId}/player`,
      serverCookies
    );
  }
}

export async function getPlayerView(
  authFetch: AuthFetch,
  itemId: string,
  serverCookies: Cookies | undefined
) {
  return getViewByPath(authFetch, `items/${itemId}/player`, serverCookies);
}

export async function fetchItemView(
  itemId: string,
  language: Language
): Promise<ViewResponse> {
  return fetchViewByPath(`items/${itemId}`, language);
}

const byAudience = (authenticated: boolean) => (list: Content) => {
  if (isList(list) || isHighlightCard(list)) {
    return (
      list.audience === undefined ||
      list.audience?.authenticated === authenticated
    );
  }

  return true;
};

export async function getViewByPath(
  authFetch: AuthFetch,
  viewPath: string,
  serverCookies: Cookies | undefined,
  authenticated = false
): Promise<ViewResponse> {
  const url = `${areenaApi.url}/v1/ui/views/${viewPath}.json`;
  const apiCookies = apiCookie(serverCookies);

  const requestInit = {
    headers: apiCookies ? { Cookie: apiCookies } : undefined,
  };

  const { data, meta } = await httpFetch<ViewResponse>({
    fetch: authFetch,
    url,
    requestInit,
    isAuthenticated: authenticated,
  });

  if (data) {
    data.tabs = data.tabs?.map((tab: Tab) => {
      tab.content = tab.content
        ?.filter(list =>
          isList(list) && typeof list.audience?.dvrSupport !== 'undefined'
            ? list.audience?.dvrSupport
            : true
        )
        .filter(byAudience(authenticated));
      return tab;
    });
  }
  return { data, meta };
}

export async function fetchViewByPath(
  viewPath: string,
  language: Language
): Promise<ViewResponse> {
  const url = `${areenaApi.url}/v1/ui/views/${viewPath}.json?${getCommonUiApiParameters(language)}`;

  const { data, meta } = await areenaApiFetch<ViewResponse>({
    url,
  });

  if (data) {
    data.tabs = data.tabs?.map((tab: Tab) => {
      tab.content = tab.content?.filter(list =>
        isList(list) && typeof list.audience?.dvrSupport !== 'undefined'
          ? list.audience?.dvrSupport
          : true
      );
      return tab;
    });
  }
  return { data, meta };
}

/**
 * @deprecated
 */
export async function getList({
  authFetch,
  url,
  paginationData,
  isAuthenticated = false,
  silentlyConsumedStatusCodes,
}: {
  authFetch: AuthFetch;
  url: string;
  paginationData: PaginationData;
  isAuthenticated?: boolean;
  silentlyConsumedStatusCodes?: number[];
}): Promise<CardResponse> {
  // country-parameter added to make API response cards include region-labels
  // actual country-value does not matter, since client's geolocation is compared to the response cards' region-labels
  const apiUrl = [
    `${url}`,
    paginationParameters(paginationData),
    'country=FI',
  ].join('&');

  return await httpFetch<CardResponse>({
    fetch: authFetch,
    url: apiUrl,
    isAuthenticated,
    silentlyConsumedStatusCodes,
  });
}

export async function getSearchResults(
  authFetch: AuthFetch,
  query: string,
  service: string,
  paginationData: PaginationData,
  isAuthenticated: boolean,
  searchPath: string | undefined
): Promise<CardResponse> {
  const normalizedQuery = String(query).toLowerCase();
  const escapedQuery = encodeURIComponent(normalizedQuery);
  const searchFilter = service ? `service=${service}` : 'service=tv';

  const parameters = [
    `episodes=true`,
    `packages=true`,
    searchFilter,
    `query=${escapedQuery}`,
    paginationParameters(paginationData),
  ].join('&');
  const url = `${searchPath}?${parameters}`;

  return await httpFetch<CardResponse>({
    fetch: authFetch,
    url,
    isAuthenticated,
  });
}

export async function getEpisodes(
  authFetch: AuthFetch,
  url: string,
  isAuthenticated: boolean
): Promise<ViewResponse> {
  const { data, meta } = await httpFetch<ViewResponse>({
    fetch: authFetch,
    url,
    isAuthenticated,
  });

  if (!data) {
    throw new Error('No data');
  }
  return { data, meta };
}

export async function getNextEpisodeFromPlaylistSource(
  authFetch: AuthFetch,
  url: string,
  isAuthenticated: boolean
): Promise<Card | undefined> {
  const { data } = await httpFetch<CardResponse>({
    fetch: authFetch,
    url: `${url}&limit=1`,
    isAuthenticated,
  });

  if (!data?.[0]) {
    throw new Error(`No data from playlistSource, url: ${url}`);
  }
  return data?.[0];
}

export const paginationParameters = (
  paginationData: PaginationData | undefined
) => {
  if (paginationData) {
    const parameters = `offset=${paginationData.offset}&limit=${paginationData.limit}`;
    return parameters;
  }
  return '';
};

export async function fetchPlayBackHistoryForItems(
  itemIds: string[],
  language: Language
): Promise<PlaybackHistoryEntries> {
  const url = `${areenaApi.url}/v2/raw/profile/history?${getCommonUiApiParameters(language)}`;
  const MAX_LIMIT = 100;

  const chunkedHistoryPromises = chunks(itemIds, MAX_LIMIT).map(chunk =>
    promiseWithTimeout(
      areenaApiFetch<HistoryResponse>({
        url: `${url}&ids=${encodeURIComponent(chunk.join(','))}`,
      }),
      2500,
      new Error('request timed out')
    ).catch(e => {
      if (!(e instanceof NotFoundError))
        logger.warn('error getting playbackhistory', e, chunk);
      return {};
    })
  );

  const historyChunks = await Promise.all(chunkedHistoryPromises);

  return Object.fromEntries(
    historyChunks.flatMap(historyChunk =>
      Object.entries(historyChunk).flatMap(([itemId, { elapsed }]) => {
        try {
          return [[itemId, isoDurationToSeconds(elapsed)]];
        } catch (error) {
          logError(error);
          return [];
        }
      })
    )
  );
}

export async function updatePlaybackHistory(
  programId: string,
  time: number,
  language: Language
): Promise<void> {
  const url = `${areenaApi.url}/v2/raw/profile/history/${encodeURIComponent(programId)}?${getCommonUiApiParameters(language)}`;
  const payload = {
    time: Math.floor(time),
  };

  return promiseWithTimeout(
    areenaApiFetch({
      url,
      ignoreBody: true,
      requestInit: {
        method: 'PUT',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    }),
    2500,
    new Error('playback history update request timed out')
  );
}

export async function fetchSimulcastSchedule(
  serviceId: string,
  language: Language,
  paginationData: PaginationData
): Promise<CardResponse> {
  const url = `${areenaApi.url}/v1/ui/services/${serviceId}/transmissions?${getCommonUiApiParameters(language)}&${paginationParameters(paginationData)}`;

  const { data, meta } = await areenaApiFetch<CardResponse>({
    url,
  });

  return { data, meta };
}
